import React, { useMemo, useState } from "react";
import { Waypoint } from "react-waypoint";
import styled from "styled-components";

const Image = styled.image<{ isLoaded: boolean }>`
  opacity: ${(props) => (props.isLoaded ? 1 : 0)};
  transition: opacity 0.5s;
`;

interface IProps {
  size: number;
  url: string;
  onLoadedChange?: (loaded: boolean, error: boolean) => void;
  className?: string;
}

const useImage = (size, url, onLoadedChange) => {
  const [isVisible, setVisible] = useState(false);
  const [isLoaded, setLoaded] = useState(false);
  const [, setError] = useState(false);

  const handlers = useMemo(
    () => ({
      loaded: () => {
        setLoaded(true);
        setError(false);
        onLoadedChange && onLoadedChange(true, false);
      },
      error: () => {
        setLoaded(false);
        setError(true);
        onLoadedChange && onLoadedChange(false, true);
      },
    }),
    [onLoadedChange]
  );

  const image = useMemo(
    () => (
      <Image
        width={size}
        height={size}
        href={url}
        preserveAspectRatio="xMinYMin slice"
        onLoad={handlers.loaded}
        onError={handlers.error}
        isLoaded={isLoaded}
      />
    ),
    [size, handlers, url, isLoaded]
  );

  return { image, isVisible, setVisible };
};

/**
 * This component loads a picture in a SVG <image> tag, only if in the viewport,
 * and with an opacity transition, AKA lazy loading
 */
const UiKitIdentityImage = ({
  size,
  url,
  onLoadedChange,
  className,
}: IProps) => {
  const { image, isVisible, setVisible } = useImage(size, url, onLoadedChange);

  return (
    <Waypoint onEnter={() => setVisible(true)}>
      <svg width={size} height={size} className={className}>
        {isVisible && image}
      </svg>
    </Waypoint>
  );
};

export default UiKitIdentityImage;
